import { useContext, useEffect, useRef, useState } from 'react'
import { MobXProviderContext } from 'mobx-react'
import moment from 'moment'
import { searchService } from 'services'

/**
 * Busca de ordens, itens e lotes no servidor para a busca do menu (barra do topo e gaveta). Espera a
 * pessoa parar de digitar, cancela a consulta anterior e devolve os resultados no mesmo formato das
 * telas do menu ({ key, label, path, to, icon, group }), para a lista e as setas do teclado.
 */

const MIN_LENGTH = 2
const DELAY = 250
const TAKE = 5

const WORK_ORDER_ROLES = [
  'realtimetracking',
  'planning',
  'admin',
  'superuser',
  'quality',
  'logistic',
]

const STATUS = {
  1: 'sem programação',
  2: 'programada',
  3: 'em execução',
  4: 'finalizada',
  5: 'pausada',
}

const qty = (value, uom) => {
  const text = (Number(value) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 3 })
  return uom ? `${text} ${uom}` : text
}

const join = (...parts) => parts.filter(Boolean).join(' · ')

export const toEntries = (data, t) => {
  if (!data) return []
  const orders = (data.workOrders || []).map((o) => ({
    key: `wo/${o.id}`,
    group: 'workOrders',
    icon: 'fas fa-clipboard-list',
    label: join(`OP ${o.code}`, o.operationCode && `op ${o.operationCode}`),
    path: join(
      o.materialCode,
      o.materialDescription,
      STATUS[o.status],
      o.workStation,
      o.status === 2 && o.beginPlanned && moment(o.beginPlanned).format('DD/MM HH:mm'),
      o.grouper && `lote ${o.grouper}`
    ),
    to: `/dashboard/work-order?id=${o.id}`,
  }))
  const materials = (data.materials || []).map((m) => ({
    key: `mat/${m.id}`,
    group: 'materials',
    icon: 'fas fa-cube',
    label: join(m.code, m.description),
    path:
      m.lots > 0
        ? join(
            `${qty(m.availableQuantity, m.uoM)} disponível`,
            m.lots === 1 ? '1 lote' : `${m.lots} lotes`
          )
        : t('nav.search.noStock', 'Sem lotes com saldo'),
    to: `/tasks/material-lots?search=${encodeURIComponent(m.code)}`,
  }))
  const lots = (data.lots || []).map((l) => ({
    key: `lot/${l.id}`,
    group: 'lots',
    icon: 'fas fa-boxes',
    label: join(`Lote ${l.batch}`, l.materialCode),
    path: join(
      l.materialDescription,
      qty(l.availableQuantity, l.uoM),
      l.location,
      l.expiryDate && `validade ${moment(l.expiryDate).format('DD/MM/YYYY')}`,
      !l.readyToUse && 'bloqueado'
    ),
    to: `/tasks/material-lots?search=${encodeURIComponent(l.batch)}${
      Number(l.availableQuantity) > 0 ? '' : '&all=1'
    }`,
  }))
  return orders.concat(materials, lots)
}

export const GROUP_LABELS = {
  menu: 'Telas e estações',
  workOrders: 'Ordens',
  materials: 'Itens',
  lots: 'Lotes',
}

export const useRemoteSearch = (query, t) => {
  const { rootStore } = useContext(MobXProviderContext) || {}
  const roles = (rootStore && rootStore.authStore && rootStore.authStore.roles) || []
  const canSearch = roles.some((role) => WORK_ORDER_ROLES.includes(role))
  const [state, setState] = useState({ entries: [], loading: false, query: '' })
  const source = useRef(null)

  useEffect(() => {
    const term = (query || '').trim()
    if (source.current) source.current.cancel()
    if (!canSearch || term.length < MIN_LENGTH) {
      setState({ entries: [], loading: false, query: term })
      return undefined
    }
    setState((prev) => ({ ...prev, loading: true, query: term }))
    const timer = setTimeout(() => {
      const current = searchService.newCancelSource()
      source.current = current
      searchService
        .search(term, TAKE, current.token)
        .then((data) => setState({ entries: toEntries(data, t), loading: false, query: term }))
        .catch((err) => {
          if (searchService.isCancel(err)) return
          setState({ entries: [], loading: false, query: term, error: true })
        })
    }, DELAY)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, canSearch])

  useEffect(() => () => source.current && source.current.cancel(), [])

  return { ...state, enabled: canSearch }
}

/** Menu primeiro (até 6 quando há ordens/itens/lotes), depois o que veio do servidor. */
export const mergeResults = (menu, remote) => {
  const list = (menu || []).map((entry) => ({ ...entry, group: 'menu' }))
  const remoteEntries = (remote && remote.entries) || []
  return (remoteEntries.length > 0 ? list.slice(0, 6) : list).concat(remoteEntries)
}
